import { GroupDoc, GroupInheritanceMap } from 'types/api/changes'
import { InheritFromGroupsRequest } from 'types/api/group'
import { normalizeGroupInheritanceMap } from 'utils/calendarInheritance'

export type CalendarGraph = Record<string, GroupInheritanceMap>

export interface CalendarGraphEdge {
  id: string
  childId: string
  parentId: string
  source: string
  target: string
}

export type CalendarEdgeHandle = 'top' | 'right' | 'bottom' | 'left'

interface EdgeCenter {
  x: number
  y: number
}

export function graphFromGroups(groups: GroupDoc[]): CalendarGraph {
  return Object.fromEntries(
    groups
      .filter((group): group is GroupDoc & { _id: string } =>
        Boolean(group._id)
      )
      .map((group) => [
        group._id,
        normalizeGroupInheritanceMap(group.ParentGroups)
      ])
  )
}

export function graphEdges(graph: CalendarGraph): CalendarGraphEdge[] {
  return Object.entries(graph).flatMap(([childId, parentGroups]) =>
    Object.keys(normalizeGroupInheritanceMap(parentGroups)).map((parentId) => ({
      id: `${parentId}->${childId}`,
      childId,
      parentId,
      source: parentId,
      target: childId
    }))
  )
}

function pickHandlePairByAxis(
  sourceCenter: EdgeCenter,
  targetCenter: EdgeCenter,
  axis: 'horizontal' | 'vertical'
): { source: CalendarEdgeHandle; target: CalendarEdgeHandle } {
  if (axis === 'horizontal') {
    return sourceCenter.x <= targetCenter.x
      ? { source: 'right', target: 'left' }
      : { source: 'left', target: 'right' }
  }

  return sourceCenter.y <= targetCenter.y
    ? { source: 'bottom', target: 'top' }
    : { source: 'top', target: 'bottom' }
}

export function graphEdgeHandles(
  sourceId: string,
  targetId: string,
  sourceCenter?: EdgeCenter,
  targetCenter?: EdgeCenter,
  reverseEdgeExists = false
): { source: CalendarEdgeHandle; target: CalendarEdgeHandle } {
  if (!sourceCenter || !targetCenter) {
    return { source: 'bottom', target: 'top' }
  }

  const dx = targetCenter.x - sourceCenter.x
  const dy = targetCenter.y - sourceCenter.y
  const primaryAxis: 'horizontal' | 'vertical' =
    Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
  const axis =
    reverseEdgeExists && sourceId > targetId
      ? primaryAxis === 'horizontal'
        ? 'vertical'
        : 'horizontal'
      : primaryAxis

  return pickHandlePairByAxis(sourceCenter, targetCenter, axis)
}

export function isSelfConnection(childId: string, parentId: string) {
  return childId === parentId
}

export function addParentConnection(
  graph: CalendarGraph,
  childId: string,
  parentId: string
): CalendarGraph {
  if (isSelfConnection(childId, parentId)) return graph

  return {
    ...graph,
    [childId]: {
      ...normalizeGroupInheritanceMap(graph[childId]),
      [parentId]: '1'
    }
  }
}

export function removeParentConnection(
  graph: CalendarGraph,
  childId: string,
  parentId: string
): CalendarGraph {
  const childParents = { ...normalizeGroupInheritanceMap(graph[childId]) }
  delete childParents[parentId]

  // Bypass: replace the removed edge with edges from the removed parent's
  // own parents (the grandparents) to the child, so the inheritance chain
  // is preserved — mirroring the bypass behavior of removeCalendarNode.
  const grandparents = normalizeGroupInheritanceMap(graph[parentId])
  Object.keys(grandparents).forEach((grandparentId) => {
    if (grandparentId !== childId) {
      childParents[grandparentId] = '1'
    }
  })

  return { ...graph, [childId]: childParents }
}

export function removeCalendarNode(
  graph: CalendarGraph,
  groupId: string
): CalendarGraph {
  const removedNodeParents = normalizeGroupInheritanceMap(graph[groupId])

  const result = Object.fromEntries(
    Object.entries(graph)
      .filter(([childId]) => childId !== groupId)
      .map(([childId, parentGroups]) => {
        const parents = { ...normalizeGroupInheritanceMap(parentGroups) }
        const hadRemovedParent = parents[groupId] === '1'

        delete parents[groupId]

        if (hadRemovedParent) {
          Object.keys(removedNodeParents).forEach((parentId) => {
            if (parentId !== childId) {
              parents[parentId] = '1'
            }
          })
        }

        return [childId, parents]
      })
  )

  return result
}

export function changedChildIds(
  base: CalendarGraph,
  draft: CalendarGraph
): string[] {
  const childIds = new Set([...Object.keys(base), ...Object.keys(draft)])
  return Array.from(childIds).filter((childId) => {
    const baseParentIds = Object.keys(
      normalizeGroupInheritanceMap(base[childId])
    ).sort()
    const draftParentIds = Object.keys(
      normalizeGroupInheritanceMap(draft[childId])
    ).sort()
    return JSON.stringify(baseParentIds) !== JSON.stringify(draftParentIds)
  })
}

export function inheritancePayload(
  base: CalendarGraph,
  draft: CalendarGraph
): InheritFromGroupsRequest {
  return Object.fromEntries(
    changedChildIds(base, draft).map((childId) => [
      childId,
      normalizeGroupInheritanceMap(draft[childId])
    ])
  )
}

export function mergeGraphIssues(
  existingIssues: Record<string, string>,
  nextIssues: Array<{ parentId: string; childId: string; message: string }>
): Record<string, string> {
  return {
    ...existingIssues,
    ...Object.fromEntries(
      nextIssues.map((issue) => [
        `${issue.parentId}->${issue.childId}`,
        issue.message
      ])
    )
  }
}

// Returns a save payload containing the groups currently on the canvas with
// their full current parent map (complete picture for groups with parents).
// A group with an empty parent map is included only if its base graph had
// parents — i.e., the emptiness was produced during this editing session
// (a removal). This avoids sending parents-with-no-children that were never
// touched by the user (e.g., an upstream calendar the user does not own
// locally but which appeared on the canvas as a parent node).
export function fullInheritancePayload(
  base: CalendarGraph,
  draft: CalendarGraph,
  visibleGroupIds: Iterable<string>
): InheritFromGroupsRequest {
  return Object.fromEntries(
    Array.from(new Set(visibleGroupIds))
      .map(
        (childId) =>
          [childId, normalizeGroupInheritanceMap(draft[childId])] as const
      )
      .filter(([childId, draftParents]) => {
        if (Object.keys(draftParents).length > 0) return true
        // Empty draft parents: only send if base had parents, meaning the
        // user cleared them during this editing session.
        const baseParents = normalizeGroupInheritanceMap(base[childId])
        return Object.keys(baseParents).length > 0
      })
  )
}
